import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { agentService } from "../../services/agentService";
import { binService } from "../../services/binService";
import { useAuth } from "../../contexts/AuthContext";
import { preAuthIntent } from "../../utils/preAuthIntent";
import {
  ArrowLeftIcon,
  StarIcon,
  MapPinIcon,
  PhoneIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";

const AgentDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [agent, setAgent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    const fetchAgent = async () => {
      try {
        const data = await agentService.getAgentById(id);
        setAgent(data);
      } catch (error) {
        setMessage("Error loading agent details");
      } finally {
        setLoading(false);
      }
    };
    fetchAgent();
  }, [id]);

  const handleAssignAgent = async () => {
    // Visitors must login before assigning an agent.
    if (!user) {
      preAuthIntent.setPendingAgent(id);
      navigate("/login");
      return;
    }

    setAssigning(true);
    setMessage("");

    try {
      await binService.assignAgent(id);
      setMessage("Agent assigned successfully! Redirecting to bin...");
      setTimeout(() => {
        navigate("/sell");
      }, 1500);
    } catch (error) {
      console.error("Error assigning agent:", error);
      setMessage(error.response?.data?.message || "Error assigning agent");
    } finally {
      setAssigning(false);
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }
  if (!agent) {
    return <div>{message || "Agent not found"}</div>;
  }
  const details = agent.agentDetails || {};
  const agentName = details.agent_name || agent.agent_name || agent.username;
  const agentImage = details.agentImage || agent.agentImage;
  const phoneNumber =
    details.phone_number || agent.phone_number || agent.phoneNumber;
  const state = agent.state || details.state;
  const lga = details.localGovernmentAddress || agent.localGovernmentAddress;
  const openHours = details.openHours || agent.openHours;
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <button
          onClick={() => navigate("/agents")}
          className="flex items-center text-green-600 hover:text-green-700 mb-4"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Back to Agents
        </button>
        <h1 className="text-3xl font-bold text-gray-900">{agentName}</h1>
        <p className="text-gray-600 mt-2">
          {state}, {lga}
        </p>
        <p className="text-gray-600">Phone: {phoneNumber}</p>
      </div>

      {/* Message */}
      {message && (
        <div
          className={`p-4 rounded-md ${
            message.includes("successfully")
              ? "bg-green-100 text-green-700 border border-green-200"
              : "bg-red-100 text-red-700 border border-red-200"
          }`}
        >
          <div className="flex items-center">
            {message.includes("successfully") ? (
              <CheckCircleIcon className="h-5 w-5 mr-2" />
            ) : (
              <ExclamationTriangleIcon className="h-5 w-5 mr-2" />
            )}
            {message}
          </div>
        </div>
      )}

      {/* Agent Details */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Agent Image and Basic Info */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-6">
            {agentImage ? (
              <img
                src={agentImage}
                alt={agentName}
                className="h-24 w-24 rounded-full object-cover mr-6"
              />
            ) : (
              <div className="h-24 w-24 rounded-full bg-gray-200 flex items-center justify-center mr-6">
                <span className="text-gray-500 text-lg">No Image</span>
              </div>
            )}
            <div>
              <h2 className="text-2xl font-bold text-gray-900">{agentName}</h2>
              <div className="flex items-center mt-2">
                <StarIcon className="h-5 w-5 text-yellow-400 mr-1" />
                <span className="text-lg text-gray-600">
                  {agent.ratings?.toFixed(1) || "No ratings"} (
                  {agent.rating_count || 0} reviews)
                </span>
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="space-y-4">
            <div className="flex items-center">
              <PhoneIcon className="h-5 w-5 text-gray-400 mr-3" />
              <div>
                <p className="text-sm font-medium text-gray-700">
                  Phone Number
                </p>
                <p className="text-gray-900">{phoneNumber}</p>
              </div>
            </div>

            <div className="flex items-start">
              <MapPinIcon className="h-5 w-5 text-gray-400 mr-3 mt-1" />
              <div>
                <p className="text-sm font-medium text-gray-700">Location</p>
                <p className="text-gray-900">{state}</p>
                <p className="text-gray-900">{lga}</p>
                {(details.streetAddress || agent.streetAddress || agent.street_address) && (
                  <p className="text-gray-900">{details.streetAddress || agent.streetAddress || agent.street_address}</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Open Hours and Additional Info */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-xl font-semibold text-gray-900 mb-4">
            Operating Hours
          </h3>

          {openHours ? (
            <div className="space-y-3">
              {Object.entries(openHours).map(([day, hours]) => (
                <div
                  key={day}
                  className="flex justify-between items-center py-2 border-b border-gray-100"
                >
                  <span className="font-medium text-gray-900 capitalize">
                    {day}
                  </span>
                  <span className="text-gray-600">
                    {hours.open && hours.close
                      ? `${hours.open} - ${hours.close}`
                      : "Not set"}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-600">Operating hours not specified</p>
          )}

          {/* Additional Information */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">
              About This Agent
            </h3>
            <div className="space-y-2 text-sm text-gray-600">
              <p>• Verified collection agent</p>
              <p>• Professional waste material validation</p>
              <p>• Secure collection and delivery</p>
              <p>• Competitive pricing and fair assessment</p>
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <button
            onClick={handleAssignAgent}
            disabled={assigning}
            className="flex-1 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white font-medium py-3 px-6 rounded-md transition-colors"
          >
            {assigning ? "Assigning Agent..." : "Assign This Agent"}
          </button>
          <button
            onClick={() => navigate("/bin")}
            className="flex-1 bg-gray-600 hover:bg-gray-700 text-white font-medium py-3 px-6 rounded-md transition-colors"
          >
            Back to My Bin
          </button>
        </div>

        <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-md">
          <h4 className="font-medium text-blue-900 mb-2">What happens next?</h4>
          <ol className="list-decimal list-inside space-y-1 text-sm text-blue-800">
            <li>This agent will be assigned to your bin</li>
            <li>The agent will contact you to schedule collection</li>
            <li>
              They will validate your materials and set accurate quantities
            </li>
            <li>Once validated, you can complete your sale</li>
          </ol>
        </div>
      </div>
    </div>
  );
};

export default AgentDetails;
